import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context';
import { TOTPInput } from '@/components/auth/TOTPInput';
import { twoFactorApi } from '@/services/two-factor';
import styles from './TwoFactorVerify.module.css';

type VerifyMode = 'totp' | 'backup';

interface LocationState {
  from?: { pathname: string };
  isPlatformLogin?: boolean;
}

export function TwoFactorVerify() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, completeLogin } = useAuth();

  const [mode, setMode] = useState<VerifyMode>('totp');
  const [backupCode, setBackupCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);
  const [error, setError] = useState<string>('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [clearTrigger, setClearTrigger] = useState(0);
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(null);

  const locationState = location.state as LocationState | null;
  const isPlatformLogin = locationState?.isPlatformLogin ?? false;

  // Redirect if fully authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = locationState?.from?.pathname || (isPlatformLogin ? '/platform' : '/');
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, locationState, isPlatformLogin]);

  const handleTOTPComplete = async (code: string) => {
    setIsVerifying(true);
    setError('');

    const response = await twoFactorApi.verify(code, rememberDevice);

    if (response.success && response.data) {
      // Complete the login process with the tokens
      if (response.data.accessToken && response.data.refreshToken) {
        completeLogin(response.data.accessToken, response.data.refreshToken);
      }

      const from = locationState?.from?.pathname || (isPlatformLogin ? '/platform' : '/');
      navigate(from, { replace: true });
    } else {
      const errorMessage = response.error?.message || 'Invalid code. Please try again.';
      setError(errorMessage);
      setClearTrigger(prev => prev + 1);

      // Handle rate limiting
      if (response.error?.details?.attemptsRemaining !== undefined) {
        setAttemptsRemaining(response.error.details.attemptsRemaining as number);
      }
    }

    setIsVerifying(false);
  };

  const handleBackupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!backupCode.trim()) {
      setError('Please enter a backup code');
      return;
    }

    setIsVerifying(true);
    setError('');

    const response = await twoFactorApi.verifyBackup(backupCode.trim());

    if (response.success && response.data) {
      // Complete the login process with the tokens
      if (response.data.accessToken && response.data.refreshToken) {
        completeLogin(response.data.accessToken, response.data.refreshToken);
      }

      const from = locationState?.from?.pathname || (isPlatformLogin ? '/platform' : '/');
      navigate(from, { replace: true });
    } else {
      setError(response.error?.message || 'Invalid backup code. Please try again.');
      setBackupCode('');
    }

    setIsVerifying(false);
  };

  const handleSwitchMode = () => {
    setMode(prev => prev === 'totp' ? 'backup' : 'totp');
    setError('');
    setBackupCode('');
    setClearTrigger(prev => prev + 1);
  };

  const handleCancel = () => {
    navigate('/login', { replace: true });
  };

  return (
    <div className={styles.page}>
      {/* Background particles */}
      <div className={styles.particlesContainer}>
        {[...Array(15)].map((_, i) => (
          <div key={i} className={styles.particle} style={{
            '--delay': `${i * 0.7}s`,
            '--x': `${Math.random() * 100}%`,
            '--duration': `${18 + Math.random() * 15}s`,
          } as React.CSSProperties} />
        ))}
      </div>

      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.lockIcon}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <h1 className={styles.title}>Two-Factor Authentication</h1>
          <p className={styles.subtitle}>
            {mode === 'totp'
              ? 'Enter the 6-digit code from your authenticator app'
              : 'Enter one of your backup codes to sign in'}
          </p>
        </div>

        {/* Content */}
        <div className={styles.content}>
          {/* Error message with attempts remaining */}
          {error && (
            <div className={styles.errorMessage}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v4M12 16h.01" />
              </svg>
              <div>
                <span>{error}</span>
                {attemptsRemaining !== null && attemptsRemaining <= 3 && (
                  <span className={styles.attemptsWarning}>
                    {attemptsRemaining} attempt{attemptsRemaining !== 1 ? 's' : ''} remaining
                  </span>
                )}
              </div>
            </div>
          )}

          {/* TOTP Mode */}
          {mode === 'totp' && (
            <div className={styles.verifyContent}>
              <div className={styles.totpContainer}>
                <TOTPInput
                  onComplete={handleTOTPComplete}
                  disabled={isVerifying}
                  error={error ? true : false}
                  clearTrigger={clearTrigger}
                  autoFocus
                />
              </div>

              {isVerifying && (
                <div className={styles.verifyingState}>
                  <div className={styles.spinner} />
                  <span>Verifying...</span>
                </div>
              )}

              {/* Remember device option */}
              <label className={styles.rememberDevice}>
                <input
                  type="checkbox"
                  checked={rememberDevice}
                  onChange={(e) => setRememberDevice(e.target.checked)}
                  className={styles.checkbox}
                />
                <span className={styles.checkboxCustom}>
                  {rememberDevice && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  )}
                </span>
                <span className={styles.rememberText}>
                  Remember this device for 30 days
                </span>
              </label>
            </div>
          )}

          {/* Backup Code Mode */}
          {mode === 'backup' && (
            <form onSubmit={handleBackupSubmit} className={styles.backupForm}>
              <div className={styles.inputGroup}>
                <label htmlFor="backupCode" className={styles.label}>
                  Backup Code
                </label>
                <input
                  id="backupCode"
                  type="text"
                  value={backupCode}
                  onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
                  placeholder="XXXX-XXXX"
                  className={styles.input}
                  disabled={isVerifying}
                  autoComplete="off"
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={isVerifying || !backupCode.trim()}
                className={styles.submitButton}
              >
                {isVerifying ? (
                  <>
                    <span className={styles.spinnerSmall} />
                    <span>Verifying...</span>
                  </>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                      <path d="M9 12l2 2 4-4" />
                    </svg>
                    <span>Verify Code</span>
                  </>
                )}
              </button>
            </form>
          )}

          {/* Mode switch */}
          <div className={styles.modeSwitch}>
            <button onClick={handleSwitchMode} className={styles.modeSwitchButton}>
              {mode === 'totp' ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                  Use a backup code instead
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                    <line x1="12" y1="18" x2="12.01" y2="18" />
                  </svg>
                  Use authenticator app instead
                </>
              )}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button onClick={handleCancel} className={styles.cancelButton}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back to Login
          </button>

          <div className={styles.helpText}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <span>Lost access? Contact your administrator</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TwoFactorVerify;
